import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import IconButton from '../components/IconButton';
import { useToast } from '../components/Toast';

export default function CodesPage() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { push: toast } = useToast();

  const fetchCodes = async () => {
    setLoading(true);
    try {
      const resp = await api.get('/api/auth/used-codes');
      setCodes(resp.data);
      setError('');
    } catch {
      setError('Failed to load registration codes.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCodes(); }, []);

  const generate = async () => {
    try {
      const resp = await api.post('/api/auth/generate-code');
      toast({ kind: 'success', message: `New code: ${resp.data.code}` });
      fetchCodes();
    } catch (err) {
      toast({ kind: 'error', message: err.response?.data?.message || 'Failed to generate code.' });
    }
  };

  const copy = (code) => {
    navigator.clipboard.writeText(code)
      .then(() => toast({ kind: 'success', message: 'Code copied.' }))
      .catch(() => toast({ kind: 'error', message: 'Copy failed.' }));
  };

  const revoke = async (id) => {
    if (!window.confirm('Revoke this code?')) return;
    try {
      await api.delete(`/api/auth/code/${id}`);
      toast({ kind: 'success', message: 'Code revoked.' });
      fetchCodes();
    } catch {
      toast({ kind: 'error', message: 'Failed to revoke code.' });
    }
  };

  const stats = {
    active: codes.filter((c) => !c.usedBy).length,
    used: codes.filter((c) => c.usedBy).length
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-[22px] font-bold tracking-tight">Registration codes</h1>
          <div className="text-xs text-surface-muted">{stats.active} active · {stats.used} used</div>
        </div>
        <button
          type="button"
          onClick={generate}
          className="px-[14px] py-2 bg-accent text-surface-base border-0 text-[11px] font-bold uppercase tracking-wider rounded-[2px]"
        >+ Generate code</button>
      </div>

      {error && <div className="text-steam-danger text-sm mb-3">{error}</div>}
      {loading && <div className="text-surface-muted text-sm mb-3">Loading…</div>}

      <div className="bg-surface-panel border border-surface-border">
        <div className="grid grid-cols-[1fr_1fr_1fr_68px] gap-3 px-[14px] py-[10px] bg-surface-header border-b border-surface-border text-[10px] font-bold uppercase tracking-[0.08em] text-surface-muted">
          <div>Code</div>
          <div>Created by</div>
          <div>Used by</div>
          <div />
        </div>
        {codes.length === 0 && !loading && (
          <div className="p-10 text-center text-surface-muted text-sm">No codes yet. Generate one to invite a user.</div>
        )}
        {codes.map((c) => {
          const used = !!c.usedBy;
          return (
            <div key={c._id} className="grid grid-cols-[1fr_1fr_1fr_68px] gap-3 px-[14px] py-[9px] border-b border-surface-base items-center text-xs">
              <div className={`font-mono text-[11px] ${used ? 'text-surface-muted line-through' : 'text-surface-text'}`}>{c.code}</div>
              <div className="text-accent">{c.createdBy?.username || '—'}</div>
              <div className={used ? 'text-steam-success' : 'text-surface-muted'}>{c.usedBy?.username || 'Unused'}</div>
              <div className="flex gap-1 justify-end">
                {!used && (
                  <>
                    <IconButton icon="📋" onClick={() => copy(c.code)} title="Copy code" />
                    <IconButton icon="✕" color="danger" onClick={() => revoke(c._id)} title="Revoke" />
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
